import axios from "axios";
import { apiService, organizationIdHeader } from "@/services/api.service";
import {
	type AttachmentRequirementDisplay,
	AttachmentRequirementDisplaySchema,
	type CreateAttachmentRequirementTagInfo,
	type EditAttachmentRequirementTagInfo,
} from "@/services/shared/attachment-requirement.types";

export type AttachmentRequirementEntityPath =
	| "jobs"
	| "tickets"
	| "assets"
	| "records";

function orgHeaders(organizationId?: string) {
	return organizationIdHeader(organizationId);
}

/**
 * GET …/types/{typeId}/attachment-requirement.
 * 404 means the type has no requirement (None).
 */
export async function getTypeAttachmentRequirement(
	entityPath: AttachmentRequirementEntityPath,
	typeId: string,
	organizationId?: string,
): Promise<AttachmentRequirementDisplay | null> {
	try {
		const response = await apiService.get<unknown>(
			`/${entityPath}/types/${typeId}/attachment-requirement`,
			{ headers: orgHeaders(organizationId) },
		);
		return AttachmentRequirementDisplaySchema.parse(response);
	} catch (error) {
		if (axios.isAxiosError(error) && error.response?.status === 404) {
			return null;
		}
		const cause =
			error && typeof error === "object" && "cause" in error
				? (error as { cause?: unknown }).cause
				: undefined;
		if (axios.isAxiosError(cause) && cause.response?.status === 404) {
			return null;
		}
		throw error;
	}
}

export async function putTypeAttachmentRequirement(
	entityPath: AttachmentRequirementEntityPath,
	typeId: string,
	minimumTotalAttachments: number,
	organizationId?: string,
): Promise<void> {
	await apiService.put(
		`/${entityPath}/types/${typeId}/attachment-requirement`,
		{ minimumTotalAttachments },
		{ headers: orgHeaders(organizationId) },
	);
}

export async function createTypeAttachmentRequirementTag(
	entityPath: AttachmentRequirementEntityPath,
	typeId: string,
	info: CreateAttachmentRequirementTagInfo,
	organizationId?: string,
): Promise<void> {
	await apiService.post(
		`/${entityPath}/types/${typeId}/attachment-requirement/tags`,
		info,
		{ headers: orgHeaders(organizationId) },
	);
}

export async function editTypeAttachmentRequirementTag(
	entityPath: AttachmentRequirementEntityPath,
	typeId: string,
	tagId: string,
	info: EditAttachmentRequirementTagInfo,
	organizationId?: string,
): Promise<void> {
	await apiService.put(
		`/${entityPath}/types/${typeId}/attachment-requirement/tags/${tagId}`,
		info,
		{ headers: orgHeaders(organizationId) },
	);
}

export function appendAttachmentRequirementTagId(
	formData: FormData,
	tagId?: string | null,
): void {
	const trimmed = tagId?.trim();
	if (trimmed) {
		formData.append("EntityAttachmentRequirementTagId", trimmed);
	}
}

export async function deleteTypeAttachmentRequirementTag(
	entityPath: AttachmentRequirementEntityPath,
	typeId: string,
	tagId: string,
	organizationId?: string,
): Promise<void> {
	await apiService.delete(
		`/${entityPath}/types/${typeId}/attachment-requirement/tags/${tagId}`,
		{ headers: orgHeaders(organizationId) },
	);
}
